import { Card, CardContent } from '@/components/ui/card';
import { Shield, Award, Users, Store, Truck } from 'lucide-react';

const features = [
  {
    icon: Shield,
    title: '100% Authentic Products',
    description: 'Every product is sourced directly from authorized distributors with verifiable authenticity.',
  },
  {
    icon: Award,
    title: 'Certified & Trusted Brands',
    description: 'We partner only with globally recognized, quality-certified supplement brands.',
  },
  {
    icon: Users,
    title: 'Athlete-Backed Recommendations',
    description: 'Our team of fitness athletes personally tests and recommends every product.',
  },
  {
    icon: Store,
    title: 'Physical Store Presence',
    description: 'Visit our store in Panitanki to see and verify products before purchase.',
  },
  {
    icon: Truck,
    title: 'Shipping Across India & Nepal',
    description: 'Fast, reliable delivery to customers across India and Nepal with tracking.',
  },
];

export const WhyChooseSection = () => {
  return (
    <section className="py-24 relative overflow-hidden section-premium-light texture-grain">
      {/* Soft Radial Glow Effects */}
      <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-primary/5 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-gold/5 rounded-full blur-[120px]" />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 text-gold font-semibold text-sm uppercase tracking-wider mb-4">
            <span className="w-8 h-px bg-gold/50" />
            WHY NUTRAFUEL
            <span className="w-8 h-px bg-gold/50" />
          </span>
          <h2 className="section-title text-foreground mb-4">
            Why Choose <span className="text-primary">NutraFuel?</span>
          </h2>
          <p className="section-subtitle mx-auto">
            We&apos;re not just selling supplements. We&apos;re building trust, one authentic product at a time.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <Card
              key={feature.title}
              className="group overflow-hidden bg-white/80 backdrop-blur-sm border border-border/50 hover:border-green-500/30 card-hover animate-fade-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <CardContent className="p-6">
                {/* Green Icon Holder */}
                <div className="w-14 h-14 mb-5 rounded-full flex items-center justify-center bg-green-500/10 border border-green-500/20 group-hover:scale-105 transition-transform">
                  <feature.icon className="w-7 h-7 text-green-500" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-green-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                
                {/* Trusted Badge */}
                <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1 bg-green-500/10 text-green-600 text-xs font-medium rounded-full border border-green-500/20">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Verified & Trusted
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom Trust Banner */}
        <div className="mt-16 relative overflow-hidden p-6 md:p-8 rounded-2xl bg-gradient-to-br from-green-50 via-white to-green-50/50 border border-green-500/20">
          {/* Beautiful Background Elements */}
          <div className="absolute top-0 left-0 w-40 h-40 bg-green-500/10 rounded-full blur-[80px]" />
          <div className="absolute bottom-0 right-0 w-48 h-48 bg-green-400/10 rounded-full blur-[100px]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-gold/5 rounded-full blur-[60px]" />
          
          {/* Decorative Shapes */}
          <div className="absolute top-4 right-8 w-3 h-3 bg-green-500/20 rounded-full" />
          <div className="absolute bottom-6 left-12 w-2 h-2 bg-green-400/30 rounded-full" />
          <div className="absolute top-1/3 left-8 w-4 h-4 border border-green-500/20 rounded-full" />
          <div className="absolute bottom-1/3 right-16 w-3 h-3 border border-green-400/20 rounded-full" />
          <div className="absolute top-8 left-1/4 w-2 h-2 bg-gold/20 rounded-full" />
          <div className="absolute bottom-4 right-1/3 w-3 h-3 border border-gold/15 rounded-full" />
          
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 relative z-10">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center bg-green-500/15 border border-green-500/30 shadow-lg shadow-green-500/10">
                <Shield className="w-8 h-8 text-green-500" strokeWidth={1.5} />
              </div>
              <div>
                <h4 className="text-xl font-bold text-foreground">Authenticity Guarantee</h4>
                <p className="text-sm text-muted-foreground">100% money-back if any product is found to be fake or tampered</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-4 py-2 bg-green-500/10 text-green-600 font-medium rounded-full text-sm border border-green-500/20">
                Verified Seller
              </span>
              <span className="px-4 py-2 bg-green-500/10 text-green-600 font-medium rounded-full text-sm border border-green-500/20">
                Authorized Distributor
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Section Separator */}
      <div className="absolute bottom-0 left-0 right-0 section-separator" />
    </section>
  );
};
